import type { JSX } from 'react'
import MediaPreview from './MediaPreview.js'
import MessageStatusIndicator from './MessageStatusIndicator.js'
import type { Message } from './types.js'
import { formatTime } from '../../utils/timezone.js'
import { formatCustomerName } from '../../utils/formatCustomerName.js'

export interface MessageBubbleProps {
  message: Message
  currentAgentId: string | null
  ratingScale: number
  failureReason?: string | undefined
  onRetry?: (() => void) | undefined
}

export default function MessageBubble(props: MessageBubbleProps): JSX.Element {
  const { message, currentAgentId, ratingScale } = props
  const isOutbound = message.direction === 'outbound'
  // AI replies are stored with no sender_agent_id and the 'ai-agent' handle.
  const isAi = isOutbound && !message.senderAgentId && message.senderHandle === 'ai-agent'
  const isOwnAgent = isOutbound && !isAi && message.senderAgentId === currentAgentId

  const senderLabel = isOutbound
    ? (isAi ? 'AI Assistant' : isOwnAgent ? 'You' : 'Agent')
    : formatCustomerName(message.senderName, message.senderHandle)

  const bubbleClass = isOutbound
    ? isOwnAgent
      ? 'bg-[#16273F] text-white'
      : 'bg-gray-100 text-gray-800'
    : 'bg-white border border-gray-200 text-gray-800'

  return (
    <div
      className={['flex flex-col gap-1', isOutbound ? 'items-end' : 'items-start'].join(' ')}
    >
      <div className="text-[10px] font-medium text-gray-400">{senderLabel}</div>
      <div
        data-testid="bubble"
        className={['max-w-md rounded-2xl px-4 py-2.5 text-sm leading-relaxed', bubbleClass].join(' ')}
      >
        {message.body && <div className="whitespace-pre-wrap">{message.body}</div>}
        {message.attachments.length > 0 && (
          <div className="mt-2 space-y-2">
            {message.attachments.map((a) => (
              <MediaPreview key={a.id} attachment={a} />
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-gray-400">
        <span title={message.createdAt}>
          {formatTime(message.createdAt)}
        </span>
        {isOutbound && <MessageStatusIndicator status={message.status} />}
        {message.rating === 'up' && (
          <span title="Visitor found this helpful" aria-label="Visitor found this helpful" role="img">👍</span>
        )}
        {message.rating === 'down' && (
          <span title="Visitor did not find this helpful" aria-label="Visitor did not find this helpful" role="img">👎</span>
        )}
        {message.csatScore != null && (
          <span title={`CSAT: ${message.csatScore}/${ratingScale}`} className="flex items-center gap-0.5 rounded bg-teal-50 px-1 py-[1px] text-[10px] font-semibold text-teal-600">
            ★ {message.csatScore}
          </span>
        )}
      </div>
      {message.status === 'failed' && (
        <div role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600 border border-red-100">
          Failed to send{props.failureReason ? `: ${props.failureReason}` : ''}.
          {props.onRetry && (
            <button
              type="button"
              className="ml-2 font-medium underline hover:text-red-700"
              onClick={props.onRetry}
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  )
}
